import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { BookmarkPlusIcon, CheckIcon, FilesIcon } from 'lucide-react';
import { BCE_SEED_V3 } from '@docgen/seed-bce';
import { Button, Modal, TextArea, TextInput } from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { cn } from '@/lib/utils';
import { notifyProposalChanged } from '@/proposals/lib/live';
import { openEditorFor } from '@/proposals/lib/editor-bridge';
import { previewDocument } from '@/proposals/lib/library-pages';
import {
  PAGE_LIBRARY_DESCRIPTION_MAX,
  PAGE_LIBRARY_MAX_PAGES,
  PAGE_LIBRARY_NAME_MAX,
  PAGE_LIBRARY_QUERY_KEY,
  PAGE_LIBRARY_VISIBILITY,
  insertLibraryItem,
  saveLibraryItem,
  useLibraryItem,
  type PageLibraryVisibility,
} from '@/proposals/lib/page-library';
import { PageThumbnail, PagesPreview } from '@/proposals/library/PageThumbnail';
import { CardBadge, CardBody, CardEyebrow, CardPrimaryButton, CardQuietButton, CardShell } from './card-primitives';

/**
 * BESTie's page library cards. The assistant finds or drafts; these cards
 * show what it found and do the write only when the person clicks:
 *
 * - `/~/draft/library-pages?proposal=&item=&name=&pages=[&at=]`: a library
 *   item with its first page drawn, Preview, and Add.
 * - `/~/draft/save-to-library?proposal=&pages=<id,id>&name=&description=&visibility=`:
 *   a save the person can edit before Save.
 *
 * When the proposal is open in the editor in this tab, both act through it
 * (pending edits saved first, the document refreshed, Add after the selected
 * page); otherwise they call the API and announce the change.
 */

const objectId = z.string().regex(/^[a-f0-9]{24}$/i);
const visibility = z
  .enum([PAGE_LIBRARY_VISIBILITY.private, PAGE_LIBRARY_VISIBILITY.team])
  .default(PAGE_LIBRARY_VISIBILITY.private);

const libraryPagesSchema = z.object({
  proposal: objectId,
  item: objectId,
  name: z.string().trim().min(1).max(PAGE_LIBRARY_NAME_MAX),
  pages: z.coerce.number().int().min(1).max(PAGE_LIBRARY_MAX_PAGES).optional(),
  at: z.coerce.number().int().min(0).optional(),
});
/** An Add card's fields. */
export type LibraryPagesDraft = z.infer<typeof libraryPagesSchema>;

const saveToLibrarySchema = z.object({
  proposal: objectId,
  pages: z
    .string()
    .transform((value) => [...new Set(value.split(',').map((part) => part.trim()).filter(Boolean))])
    .pipe(z.array(z.string().min(1)).min(1).max(PAGE_LIBRARY_MAX_PAGES)),
  name: z.string().trim().max(PAGE_LIBRARY_NAME_MAX).default(''),
  description: z.string().trim().max(PAGE_LIBRARY_DESCRIPTION_MAX).default(''),
  visibility,
});
/** A Save card's fields. */
export type SaveToLibraryDraft = z.infer<typeof saveToLibrarySchema>;

/** Parses an Add card's link fields, or null when they don't add up. */
export function planLibraryPagesDraft(params: Readonly<Record<string, string>>): LibraryPagesDraft | null {
  const parsed = libraryPagesSchema.safeParse(params);
  return parsed.success ? parsed.data : null;
}

/** Parses a Save card's link fields, or null when they don't add up. */
export function planSaveToLibraryDraft(params: Readonly<Record<string, string>>): SaveToLibraryDraft | null {
  const parsed = saveToLibrarySchema.safeParse(params);
  return parsed.success ? parsed.data : null;
}

/** Card thumbnail width; the height follows the letter page. */
const CARD_THUMB_WIDTH_PX = 96;
/** Preview page width, as in the editor's picker. */
const PREVIEW_WIDTH_PX = 600;

const VISIBILITY_LABEL: Readonly<Record<PageLibraryVisibility, string>> = {
  [PAGE_LIBRARY_VISIBILITY.private]: 'Only me',
  [PAGE_LIBRARY_VISIBILITY.team]: 'Whole team',
};

function Unreadable(): React.ReactElement {
  return (
    <span className="my-1 block text-admin-micro text-admin-muted italic">
      The assistant's draft could not be read. Ask it to try again.
    </span>
  );
}

/** "Pages from the library": a found item, drawn with this proposal's data when it's open. */
export function LibraryPagesCard({ params }: { readonly params: Readonly<Record<string, string>> }): React.ReactElement {
  const draft = planLibraryPagesDraft(params);
  if (!draft) return <Unreadable />;
  return <LibraryPagesCardBody draft={draft} />;
}

function LibraryPagesCardBody({ draft }: { readonly draft: LibraryPagesDraft }): React.ReactElement {
  const queryClient = useQueryClient();
  const [dismissed, setDismissed] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const detail = useLibraryItem(draft.item);
  // Thumbnails show this deal's data when the proposal is open; the sample data otherwise.
  const base = openEditorFor(draft.proposal)?.currentDoc() ?? BCE_SEED_V3;

  const add = useMutation({
    mutationFn: async () => {
      const target = { id: draft.item, name: detail.data?.name ?? draft.name };
      const editor = openEditorFor(draft.proposal);
      if (editor) {
        await editor.library.insert(target, draft.at);
        return;
      }
      await insertLibraryItem(draft.proposal, { itemId: draft.item, ...(draft.at !== undefined ? { atIndex: draft.at } : {}) });
      notifyProposalChanged(draft.proposal);
    },
    onSuccess: async () => {
      setPreviewing(false);
      await queryClient.invalidateQueries({ queryKey: PAGE_LIBRARY_QUERY_KEY });
    },
  });

  const name = detail.data?.name ?? draft.name;
  if (dismissed) return <span className="my-1 block text-admin-micro text-admin-muted italic">Library pages dismissed.</span>;

  const pageCount = detail.data?.pageCount ?? draft.pages;
  const meta = [
    pageCount === undefined ? '' : `${pageCount} ${pageCount === 1 ? 'page' : 'pages'}`,
    detail.data?.ownerName ?? '',
    detail.data ? (detail.data.visibility === PAGE_LIBRARY_VISIBILITY.team ? 'Team' : 'Private') : '',
  ].filter((part) => part !== '');
  const preview = detail.data ? previewDocument(base, detail.data.pages, detail.data.dataFragment) : null;

  return (
    <CardShell>
      <CardBody>
        <span className="flex items-start gap-3">
          <CardBadge>
            <FilesIcon aria-hidden className="size-[18px]" />
          </CardBadge>
          <span className="min-w-0 flex-1">
            <CardEyebrow>{add.isSuccess ? 'Added to the proposal' : 'Pages from the library'}</CardEyebrow>
            <span className="mt-0.5 block font-bold text-portal-ink">{name}</span>
            {meta.length > 0 ? <span className="block text-admin-micro text-admin-muted">{meta.join(' · ')}</span> : null}
          </span>
          {add.isSuccess ? <CheckIcon aria-hidden className="size-4 shrink-0 text-emerald-600" /> : null}
        </span>

        <span className="mt-2.5 flex items-start gap-3">
          {preview ? (
            <PageThumbnail doc={{ ...preview, pages: preview.pages.slice(0, 1) }} width={CARD_THUMB_WIDTH_PX} />
          ) : (
            <span aria-hidden className="block h-[124px] w-[96px] shrink-0 animate-pulse rounded-[4px] bg-admin-subtle" />
          )}
          <span className="min-w-0 flex-1 text-admin-micro text-portal-ink">
            {detail.isError ? (
              <span className="text-red-600">These pages aren't available. They may have been deleted or made private.</span>
            ) : (
              detail.data?.description
            )}
          </span>
        </span>

        {add.isError ? (
          <span className="mt-2 block text-admin-micro font-semibold text-red-600">Couldn't add these pages. {errorMessage(add.error)}</span>
        ) : null}

        {add.isSuccess ? (
          openEditorFor(draft.proposal) ? null : (
            <span className="mt-2.5 block">
              <Link to={`/proposals/${draft.proposal}`} className="text-admin-micro font-bold text-brand no-underline hover:underline">
                Open proposal
              </Link>
            </span>
          )
        ) : (
          <span className="mt-2.5 flex items-center gap-2">
            <CardPrimaryButton onClick={() => add.mutate()} disabled={add.isPending || !detail.data}>
              {add.isPending ? 'Adding…' : 'Add'}
            </CardPrimaryButton>
            <CardQuietButton onClick={() => setPreviewing(true)} disabled={!detail.data}>
              Preview
            </CardQuietButton>
            <CardQuietButton onClick={() => setDismissed(true)} disabled={add.isPending}>
              Dismiss
            </CardQuietButton>
          </span>
        )}
      </CardBody>
      {previewing && preview ? (
        <Modal
          open
          title={name}
          {...(meta.length > 0 ? { subtitle: meta.join(' · ') } : {})}
          onClose={() => setPreviewing(false)}
          dismissible={!add.isPending}
          className="max-w-3xl"
          phoneFullScreen
          actions={
            <>
              <Button variant="secondary" onClick={() => setPreviewing(false)} disabled={add.isPending}>
                Back
              </Button>
              <Button onClick={() => add.mutate()} disabled={add.isPending}>
                {add.isPending ? 'Adding…' : 'Add'}
              </Button>
            </>
          }
        >
          {detail.data?.description ? <p className="mb-3">{detail.data.description}</p> : null}
          <div className="flex max-h-[65vh] justify-center overflow-y-auto rounded-portal-card bg-desk p-4">
            <PagesPreview doc={preview} width={PREVIEW_WIDTH_PX} />
          </div>
        </Modal>
      ) : null}
    </CardShell>
  );
}

/** "Save to library": the assistant's draft, editable, saved only on Save. */
export function SaveToLibraryCard({ params }: { readonly params: Readonly<Record<string, string>> }): React.ReactElement {
  const draft = planSaveToLibraryDraft(params);
  if (!draft) return <Unreadable />;
  return <SaveToLibraryCardBody draft={draft} />;
}

function SaveToLibraryCardBody({ draft }: { readonly draft: SaveToLibraryDraft }): React.ReactElement {
  const queryClient = useQueryClient();
  const nameId = useId();
  const descriptionId = useId();
  const [dismissed, setDismissed] = useState(false);
  const [name, setName] = useState(draft.name);
  const [description, setDescription] = useState(draft.description);
  const [shareWith, setShareWith] = useState<PageLibraryVisibility>(draft.visibility);

  const save = useMutation({
    mutationFn: async () => {
      const values = { name: name.trim(), description: description.trim(), visibility: shareWith };
      const editor = openEditorFor(draft.proposal);
      if (editor) return editor.library.save(draft.pages, values);
      return saveLibraryItem({
        proposalId: draft.proposal,
        pageIds: draft.pages,
        name: values.name,
        ...(values.description ? { description: values.description } : {}),
        visibility: values.visibility,
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PAGE_LIBRARY_QUERY_KEY }),
  });

  if (dismissed) return <span className="my-1 block text-admin-micro text-admin-muted italic">Save to library dismissed.</span>;

  const pageCount = `${draft.pages.length} ${draft.pages.length === 1 ? 'page' : 'pages'}`;

  if (save.isSuccess) {
    return (
      <CardShell>
        <CardBody>
          <span className="flex items-start gap-3">
            <CardBadge>
              <BookmarkPlusIcon aria-hidden className="size-[18px]" />
            </CardBadge>
            <span className="min-w-0 flex-1">
              <CardEyebrow>Saved to the library</CardEyebrow>
              <span className="mt-0.5 block font-bold text-portal-ink">{save.data.name}</span>
              <span className="block text-admin-micro text-admin-muted">
                {pageCount} · {save.data.visibility === PAGE_LIBRARY_VISIBILITY.team ? 'The whole team can use it.' : 'Only you can see it.'} Add it to any
                proposal from Pages → From library.
              </span>
            </span>
            <CheckIcon aria-hidden className="size-4 shrink-0 text-emerald-600" />
          </span>
        </CardBody>
      </CardShell>
    );
  }

  return (
    <CardShell>
      <CardBody>
        <span className="flex items-start gap-3">
          <CardBadge>
            <BookmarkPlusIcon aria-hidden className="size-[18px]" />
          </CardBadge>
          <span className="min-w-0 flex-1">
            <CardEyebrow>Save to library</CardEyebrow>
            <span className="mt-0.5 block font-bold text-portal-ink">{pageCount} from this proposal</span>
          </span>
        </span>

        <span className="mt-2.5 block space-y-2">
          <label htmlFor={nameId} className="block text-admin-micro font-semibold text-admin-muted">
            Name
            <span className="ml-0.5 text-rose-600" title="Required">
              <span aria-hidden="true">*</span>
              <span className="sr-only"> (required)</span>
            </span>
          </label>
          <TextInput id={nameId} density="compact" value={name} maxLength={PAGE_LIBRARY_NAME_MAX} onChange={(event) => setName(event.target.value)} />
          <label htmlFor={descriptionId} className="block text-admin-micro font-semibold text-admin-muted">
            Description
          </label>
          <TextArea
            id={descriptionId}
            rows={2}
            value={description}
            maxLength={PAGE_LIBRARY_DESCRIPTION_MAX}
            onChange={(event) => setDescription(event.target.value)}
          />
          <span className="block text-admin-micro text-admin-muted">People find pages by searching names and descriptions.</span>
          <span role="group" aria-label="Who can use these pages" className="inline-flex rounded-md border border-admin-line p-0.5">
            {[PAGE_LIBRARY_VISIBILITY.private, PAGE_LIBRARY_VISIBILITY.team].map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={shareWith === option}
                onClick={() => setShareWith(option)}
                className={cn(
                  'rounded-[5px] px-2.5 py-1 text-admin-micro',
                  shareWith === option ? 'bg-brand font-bold text-white' : 'text-admin-muted hover:text-portal-ink',
                )}
              >
                {VISIBILITY_LABEL[option]}
              </button>
            ))}
          </span>
        </span>

        {save.isError ? (
          <span className="mt-2 block text-admin-micro font-semibold text-red-600">Couldn't save these pages. {errorMessage(save.error)}</span>
        ) : null}

        <span className="mt-2.5 flex items-center gap-2">
          <CardPrimaryButton onClick={() => save.mutate()} disabled={save.isPending || name.trim() === ''}>
            {save.isPending ? 'Saving…' : 'Save to library'}
          </CardPrimaryButton>
          <CardQuietButton onClick={() => setDismissed(true)} disabled={save.isPending}>
            Dismiss
          </CardQuietButton>
        </span>
      </CardBody>
    </CardShell>
  );
}
